/**
 * Search template.
 */
import Animator from '@components/elements/animator/animator';
import Focus from '@shared/types/enums/focus';
import FocusUtils from '@shared/utils/focus';
import Image from '@components/atoms/images/descriptionImage';
import SearchInput from '../sections/search/searchInput';
import SearchKeyboard from '../sections/search/searchKeyboard';
import SearchResults from '../sections/search/searchResults';
import useSearch from '@shared/components/utils/hooks/useSearch';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { appSelector } from '@shared/store/slices/app.slice';
import { selectMenuCollapse } from '@shared/store/slices/menu.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { store } from '@shared/store/store';
import { useAppSelector } from '@shared/store/hooks';
import SearchMaskLeft from '@components/atoms/mask/searchMaskLeft';
import SearchMaskBottom from '@components/atoms/mask/searchMaskBottom';

/**
 * Search template. Takes care of the search functionality and the search UI.
 * Includes the search input, the search keyboard, the search results and the search queries.
 */
export default function SearchTemplate() {
  // Selectors
  const { status } = useAppSelector(appSelector);
  const menuExpanded = !useAppSelector(selectMenuCollapse);

  // Search hook
  const {
    background,
    error,
    input,
    message,
    queries,
    rails,
    searching,
    selection,
    totalResults,
    clearAction,
    searchAction,
    onChangeInput,
    onDeleteInput,
    onMouseEnter,
    outOfBoundsAction,
    setBackground,
  } = useSearch();

  // Components data
  const actions = {
    clearAction,
    searchAction,
    onChangeInput,
    onDeleteInput,
    outOfBoundsAction,
  };

  // Keyboard hooks
  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      const { focus: focusSlice } = store.getState();
      if (FocusUtils.evaluateFocus(Focus.FLOATING_MENU)) return;
      switch (key) {
        case 'BACK':
          if (FocusUtils.evaluateFocus(Focus.RAILS_LIST)) {
            store.dispatch(setFocus(Focus.SEARCH_KEYBOARD));
            return;
          }
          if (focusSlice.focus === Focus.QUERY_LIST) {
            store.dispatch(setFocus(Focus.SEARCH_KEYBOARD));
            return;
          }
          store.dispatch(setFocus(Focus.SEARCH));
          break;
        case 'RIGHT':
          if (
            focusSlice.focus === Focus.SEARCH ||
            focusSlice.focus === Focus.LOGIN
          ) {
            store.dispatch(setFocus(Focus.SEARCH_KEYBOARD));
          }
          break;
      }
    },
  });

  // Render
  return (
    <>
      {status.description && (
        <>
          <Image src={background} reducedSize={true} />
          <SearchMaskLeft />
          <SearchMaskBottom />
        </>
      )}
      <Animator
        collapse={{ horizontal: menuExpanded }}
        onMouseEnter={onMouseEnter}
        type={Animator.Type.SEARCH}
      >
        <SearchKeyboard actions={actions} queries={queries} />
        <SearchResults
          key={selection}
          message={message}
          rails={rails}
          searching={searching}
          setBackground={setBackground}
        />
        <SearchInput
          input={input}
          error={error}
          results={totalResults}
          searching={searching}
          selection={selection}
        />
      </Animator>
    </>
  );
}
